import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, X, Link2 } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import usePermissionsStore from '../../stores/permissionsStore'
import useInstagramConnectionStore from '../../stores/instagramConnectionStore'
import { isInstagramGraphApiEnabled } from '../../utils/featureFlags'
import { Button } from '../ui'

// Session-scoped dismiss: hides for the current browser session but reappears on next
// login, and is cleared automatically once the underlying tokens are no longer expiring.
const DISMISS_KEY = 'enhancedApiTokenBannerDismissed'

/**
 * App-wide banner warning that one or more Instagram Enhanced API tokens are expiring
 * or expired. Driven by tokenStatus (set by the auth token-refresh scheduler only when a
 * token couldn't be auto-refreshed), so it won't nag users whose tokens refresh cleanly.
 */
const EnhancedApiTokenBanner: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { loadPermissions, hasFeature } = usePermissionsStore()
  const { connections, loadConnections } = useInstagramConnectionStore()
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === 'true'
  )

  useEffect(() => {
    if (user?.organizationId) {
      loadPermissions(user.organizationId)
      loadConnections(user.organizationId)
    }
  }, [user?.organizationId, loadPermissions, loadConnections])

  const featureOn = isInstagramGraphApiEnabled() && hasFeature('instagram_graph_api')
  const alerts = featureOn
    ? connections.filter(c => c.tokenStatus === 'expired' || c.tokenStatus === 'expiring_soon')
    : []

  // If the alerts have resolved (tokens refreshed), clear the dismiss so it re-shows next time.
  useEffect(() => {
    if (alerts.length === 0 && dismissed) {
      sessionStorage.removeItem(DISMISS_KEY)
      setDismissed(false)
    }
  }, [alerts.length, dismissed])

  if (alerts.length === 0 || dismissed) return null

  const hasExpired = alerts.some(c => c.tokenStatus === 'expired')
  const named = (hasExpired ? alerts.filter(c => c.tokenStatus === 'expired') : alerts)
    .map(c => `@${c.providerUsername}`)
    .join(', ')

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true')
    setDismissed(true)
  }

  return (
    <div
      className={`flex items-start gap-3 px-6 py-2.5 text-sm border-b ${
        hasExpired
          ? 'bg-red-50 border-red-200 text-red-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {hasExpired ? (
          <span>
            <span className="font-semibold">Enhanced API access has expired</span> for {named}.
            Views, shares, saves, and audience demographics are no longer being collected — reconnect to restore them.
          </span>
        ) : (
          <span>
            <span className="font-semibold">Enhanced API access is expiring soon</span> for {named}.
            Reconnect before it expires or you'll stop collecting views, shares, saves, and audience demographics.
          </span>
        )}
      </div>
      <Button
        variant="outline"
        size="xs"
        onClick={() => navigate('/settings?tab=accounts')}
        className="flex-shrink-0"
      >
        <Link2 className="h-3 w-3 mr-1" />
        Reconnect
      </Button>
      <button
        onClick={dismiss}
        className="flex-shrink-0 ml-1 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export default EnhancedApiTokenBanner
